import React, { useState, useEffect, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Bounding box in percentage coordinates (0-100)
export interface BoundingBox {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

interface PdfViewerProps {
  url: string | null;
  pageNumber?: number;
  boundingBox?: BoundingBox | null;
  isLoading?: boolean;
  error?: string | null;
}

export const PdfViewer: React.FC<PdfViewerProps> = ({
  url,
  pageNumber = 1,
  boundingBox = null,
  isLoading = false,
  error = null,
}) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(pageNumber);
  const [scale, setScale] = useState<number>(1.0);
  const [pageWidth, setPageWidth] = useState<number>(0);
  const [pageHeight, setPageHeight] = useState<number>(0);

  // Sync currentPage with pageNumber prop
  useEffect(() => {
    if (pageNumber && pageNumber >= 1) {
      setCurrentPage(pageNumber);
    }
  }, [pageNumber]);

  const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    if (currentPage > numPages) {
      setCurrentPage(numPages);
    }
  }, [currentPage]);

  const onPageLoadSuccess = useCallback(({ width, height }: { width: number; height: number }) => {
    setPageWidth(width);
    setPageHeight(height);
  }, []);

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(numPages, prev + 1));
  };

  const handleZoomIn = () => {
    setScale((prev) => Math.min(3.0, prev + 0.25));
  };

  const handleZoomOut = () => {
    setScale((prev) => Math.max(0.5, prev - 0.25));
  };

  const handleResetZoom = () => {
    setScale(1.0);
  };

  // Render bounding box overlay
  const renderBoundingBoxOverlay = () => {
    // Only show bounding box on the page it was intended for
    if (!boundingBox || pageWidth === 0 || pageHeight === 0 || currentPage !== pageNumber) return null;

    // Detect if coordinates are in 0-1 range (normalized) or 0-100 range (percentage)
    // If all coords are <= 1, assume normalized and convert to percentage
    const isNormalized = boundingBox.x0 <= 1 && boundingBox.y0 <= 1 &&
                         boundingBox.x1 <= 1 && boundingBox.y1 <= 1;
    const multiplier = isNormalized ? 1 : 100;

    // Convert coordinates to pixels (pageWidth/pageHeight are already scaled)
    const left = (boundingBox.x0 / multiplier) * pageWidth;
    const top = (boundingBox.y0 / multiplier) * pageHeight;
    const width = ((boundingBox.x1 - boundingBox.x0) / multiplier) * pageWidth;
    const height = ((boundingBox.y1 - boundingBox.y0) / multiplier) * pageHeight;

    return (
      <div
        className="bounding-box-overlay"
        style={{
          position: 'absolute',
          left: `${left}px`,
          top: `${top}px`,
          width: `${width}px`,
          height: `${height}px`,
          pointerEvents: 'none',
        }}
      />
    );
  };

  if (isLoading) {
    return (
      <div className="file-viewer">
        <div className="file-viewer-loading">
          <ProgressSpinner />
          <span>Loading preview...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="file-viewer">
        <Message severity="error" text={error} />
      </div>
    );
  }

  if (!url) {
    return (
      <div className="file-viewer">
        <Message severity="info" text="Select a row to preview its source file" />
      </div>
    );
  }

  return (
    <div className="file-viewer">
      <div className="pdf-controls">
        <div className="button-group">
          <Button
            icon="ms-out ms-out-chevron-left"
            onClick={handlePrevPage}
            disabled={currentPage <= 1}
            tooltip="Previous page"
            link
          />
          <span className="page-indicator">
            {currentPage} / {numPages || '?'}
          </span>
          <Button
            icon="ms-out ms-out-chevron-right"
            onClick={handleNextPage}
            disabled={currentPage >= numPages}
            tooltip="Next page"
            link
          />
        </div>
        <div className="button-group">
          <Button
            icon="ms-out ms-out-zoom-out"
            onClick={handleZoomOut}
            disabled={scale <= 0.5}
            tooltip="Zoom out"
            link
          />
          <span>{Math.round(scale * 100)}%</span>
          <Button
            icon="ms-out ms-out-zoom-in"
            onClick={handleZoomIn}
            disabled={scale >= 3.0}
            tooltip="Zoom in"
            link
          />
          <Button
            icon="ms-out ms-out-fit-screen"
            onClick={handleResetZoom}
            tooltip="Reset zoom"
            link
          />
        </div>
      </div>

      <div className="pdf-container">
        <Document
          file={url}
          onLoadSuccess={onDocumentLoadSuccess}
          loading={
            <div className="pdf-loading">
              <ProgressSpinner />
            </div>
          }
          error={
            <Message severity="error" text="Failed to load PDF" />
          }
        >
          <div className="pdf-page-wrapper">
            <Page
              pageNumber={currentPage}
              scale={scale}
              onLoadSuccess={onPageLoadSuccess}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
            {renderBoundingBoxOverlay()}
          </div>
        </Document>
      </div>
    </div>
  );
};
